import React from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { CheckCircle2, XCircle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { OutcomesInterface } from "@components/ModalCst";
import { ButtonTertiary } from "src/UI/ButtonTertiary";

const SUCCESS_CODE = "OK";

interface OutcomeModalV2CstProps {
  open: boolean;
  outcome: OutcomesInterface;
  successTitle?: React.ReactNode;
  errorTitle?: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: React.ReactNode;
  retryLabel?: React.ReactNode;
  hideCloseButton?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const OutcomeModalV2Cst: React.FC<OutcomeModalV2CstProps> = ({
  open,
  outcome,
  successTitle,
  errorTitle,
  description,
  confirmLabel,
  retryLabel,
  hideCloseButton = false,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("modal");

  const keys = Object.keys(outcome || {});
  const isPending = keys.some((key) => outcome[key]?.code === undefined);
  const failedKeys = keys.filter(
    (key) =>
      outcome[key]?.code !== undefined && outcome[key]?.code !== SUCCESS_CODE,
  );
  const isNotOk = failedKeys.length > 0;
  const isPartial = isNotOk && failedKeys.length < keys.length;
  const isTotalFailure = !isPending && isNotOk && !isPartial;
  const errorColor = theme.palette.badge.error.border;

  const renderStatusIcon = () => {
    if (isPending) return <CircularProgress size={40} />;
    if (isNotOk) return <XCircle size={48} color={errorColor} />;
    return <CheckCircle2 size={48} color={theme.palette.primary.main} />;
  };

  const headline = () => {
    if (isPending) return t("outcome-pending-title");
    if (isNotOk) return errorTitle || t("outcome-error-title");
    return successTitle || t("outcome-success-title");
  };

  const normalizeText = (value: unknown) =>
    typeof value === "string" ? value.trim().toLowerCase() : "";

  const subtitle = (currentHeadline: React.ReactNode) => {
    if (isPending) return undefined;
    if (!isNotOk) return description;
    const headlineKey = normalizeText(currentHeadline);
    const messages = failedKeys
      .map((key) => outcome[key]?.message)
      .filter(
        (message) =>
          !!normalizeText(message) && normalizeText(message) !== headlineKey,
      );
    return messages.length > 0 ? messages.join(" · ") : undefined;
  };

  const headlineText = headline();
  const subtitleText = subtitle(headlineText);

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      {hideCloseButton || isTotalFailure ? (
        <Box sx={{ paddingTop: "24px" }} />
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "12px 12px 0",
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label="close"
            sx={{ color: theme.palette.text.primary }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      )}

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          padding: "0 24px 8px",
          textAlign: "center",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {renderStatusIcon()}
        </Box>

        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          {headlineText}
        </Typography>

        {subtitleText && (
          <Typography
            sx={{
              fontSize: "14px",
              color: theme.palette.text.primary,
              overflowWrap: "anywhere",
            }}
          >
            {subtitleText}
          </Typography>
        )}

        {isPartial && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              width: "100%",
              mt: 1,
            }}
          >
            {keys.map((key) => (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                }}
              >
                {outcome[key]?.code === SUCCESS_CODE ? (
                  <CheckCircle2
                    size={20}
                    color={theme.palette.primary.main}
                    style={{ flexShrink: 0 }}
                  />
                ) : (
                  <XCircle
                    size={20}
                    color={errorColor}
                    style={{ flexShrink: 0 }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: theme.palette.text.primary,
                    overflowWrap: "anywhere",
                  }}
                >
                  {outcome[key]?.title || key}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      {!isPending && (
        <>
          <Divider sx={{ mx: 3, opacity: 0.6 }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "16px 24px 20px",
            }}
          >
            <ButtonTertiary
              size="md"
              onClick={isNotOk && !isPartial ? onClose : onConfirm}
            >
              {isNotOk && !isPartial
                ? retryLabel || t("cancel-outcome-button")
                : confirmLabel || t("submit-outcome-button")}
            </ButtonTertiary>
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default OutcomeModalV2Cst;
